import { Button, Card, H5, H6, Icon, UL } from '@blueprintjs/core';
import * as React from 'react';
import { Tooltip } from 'vet-bones/components';
import { getIntentClassName } from 'vet-bones/utils';

import { isAP, isEU } from 'src/ui/constants/environment';
import { GET_STARTED_CTA } from 'src/ui/constants/testIds';
import { AnyFunc } from 'src/ui/constants/types';
import { InstanceFlavors } from 'src/ui/features/connection';
import { components } from 'src/ui/templates/copy';

interface ProductCardProps {
  flavor: InstanceFlavors;
  recommended?: boolean;
  addToCart: AnyFunc;
  disabled?: boolean;
  disabledTooltip?: string;
}

export type ProductInfo = {
  tag: string;
  subTag?: string;
  title: string;
  description: string;
  cta: string;
  ctaDisabled?: boolean;
  ctaDisabledTooltip?: string;
  priceText: string;
  features: { title: string; list: string[] };
};

export const getProductInfo = (flavor: string): ProductInfo => {
  const defaults: ProductInfo = {
    tag: '',
    title: '',
    description: '',
    cta: '',
    priceText: '',
    features: { title: '', list: [] },
  };

  if (flavor === InstanceFlavors.MICRO) {
    let ctaDisabled = false;
    let ctaDisabledTooltip = '';
    if (isAP()) {
      ctaDisabled = true;
      ctaDisabledTooltip =
        components.getStarted.editions.free.ctaDisabledTooltip.ap;
    } else if (isEU()) {
      ctaDisabled = true;
      ctaDisabledTooltip =
        components.getStarted.editions.free.ctaDisabledTooltip.eu;
    }
    return {
      ...components.getStarted.editions.free,
      cta: ctaDisabled
        ? components.getStarted.editions.free.ctaDisabled
        : components.getStarted.editions.free.cta,
      ctaDisabled,
      ctaDisabledTooltip,
    };
  }
  if (flavor === InstanceFlavors.SMALL_VBX) {
    const ctaDisabled = isAP() || isEU();
    return {
      ...components.getStarted.editions.essentials,
      cta: ctaDisabled
        ? components.getStarted.editions.essentials.ctaDisabled
        : components.getStarted.editions.essentials.cta,
      ctaDisabled,
    };
  }
  if (flavor === InstanceFlavors.MEDIUM) {
    return components.getStarted.editions.enterprise;
  }

  return defaults;
};

export const ProductInfoPanel: React.VFC<{
  flavor: string;
}> = ({ flavor }) => {
  const copy = getProductInfo(flavor);
  return (
    <div className="get-started__product-card-content">
      <p>
        {copy.tag}
        {copy.subTag ? (
          <>
            <br />
            <br />
            {copy.subTag}
          </>
        ) : null}
      </p>
      <span className="price">{copy.priceText}</span>
      <H6>{copy.features.title}</H6>
      <UL className="get-started__product-card-content-list">
        {copy.features.list.map((feature) => (
          <li key={feature}>
            <Icon
              className="get-started__product-card-content-list-item-icon"
              icon="endorsed"
            />
            {feature}
          </li>
        ))}
      </UL>
    </div>
  );
};

export const ProductCard: React.VFC<ProductCardProps> = ({
  flavor,
  recommended,
  addToCart,
  disabled,
  disabledTooltip,
}) => {
  const className = recommended
    ? 'get-started__product-card recommended'
    : 'get-started__product-card';

  const getAddToCartButton = (
    cta: string,
    ctaDisabled?: boolean,
    ctaDisabledTooltip?: string
  ) => {
    const isDisabled = ctaDisabled || disabled;
    // The override tooltip takes precedence when present (pricing being
    // unavailable blocks checkout entirely), but otherwise fall back to the
    // region-based tooltip rather than dropping it.
    const tooltip = disabledTooltip ?? ctaDisabledTooltip;
    return (
      <Tooltip content={tooltip || ''} disabled={!isDisabled || !tooltip}>
        <Button
          onClick={addToCart}
          className={getIntentClassName('sd-green')}
          data-testid={GET_STARTED_CTA(flavor)}
          disabled={isDisabled}
        >
          {cta}
        </Button>
      </Tooltip>
    );
  };

  const product = getProductInfo(flavor);

  return (
    <Card className={className} elevation={2}>
      <div className="get-started__product-card-header">
        <div className="get-started__product-card-header-content">
          <H5 className="name">{product.title}</H5>
        </div>
      </div>
      <ProductInfoPanel flavor={flavor} />
      <div className="get-started__product-card-footer">
        {getAddToCartButton(
          product.cta,
          product.ctaDisabled,
          product.ctaDisabledTooltip
        )}
      </div>
    </Card>
  );
};
